"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useSession, authenticatedFetch } from "@/lib/auth-client";

interface CreateLobbyModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function CreateLobbyModal({ isOpen, onClose }: CreateLobbyModalProps) {
    const router = useRouter();
    const { data: session } = useSession();

    const [mode, setMode] = useState<'live' | 'async'>('live');
    const [maxPlayers, setMaxPlayers] = useState(20);
    const [maxRounds, setMaxRounds] = useState(8);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const handleCreate = async () => {
        if (!session?.user) {
            // Redirect to login with a return path that auto-opens this modal after onboarding
            const returnPath = encodeURIComponent('/game?openLobbyModal=true');
            router.push(`/login?redirect=${returnPath}`);
            return;
        }

        setIsLoading(true);
        setError("");

        try {
            const res = await authenticatedFetch('/api/v1/lobbies', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mode,
                    maxPlayers,
                    gameConfig: { maxRounds }
                })
            });

            const data = await res.json();

            if (res.ok) {
                router.push(`/lobby/${data.lobby.id}`);
                onClose();
            } else {
                setError(data.error || "Failed to create lobby");
            }
        } catch (e) {
            console.error(e);
            setError("Network error. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[var(--surface-dark)] border border-white/10 rounded-3xl p-6 w-full max-w-md shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] opacity-50" />

                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-black text-white">Host Private Game</h2>
                            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-gray-400">
                                ✕
                            </button>
                        </div>

                        {!!error && (
                            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl mb-6 text-sm">
                                {typeof error === 'object' ? JSON.stringify(error) : String(error)}
                            </div>
                        )}

                        <div className="space-y-6">
                            {/* Mode Selection */}
                            <div>
                                <label className="block text-sm font-bold text-gray-400 mb-2 uppercase tracking-wider">Game Mode</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={() => setMode('live')}
                                        className={`p-3 rounded-xl border text-left transition-all ${mode === 'live' ? 'bg-indigo-500/20 border-indigo-500 text-white' : 'bg-black/40 border-white/5 text-gray-500 hover:border-white/20'}`}
                                    >
                                        <div className="font-bold flex items-center gap-2">
                                            <span className={mode === 'live' ? 'text-indigo-400' : ''}>⚡</span> Live
                                        </div>
                                        <div className="text-xs mt-1 opacity-70">Play together in real-time. Minimum 4 players.</div>
                                    </button>
                                    <button
                                        onClick={() => setMode('async')}
                                        className={`p-3 rounded-xl border text-left transition-all ${mode === 'async' ? 'bg-purple-500/20 border-purple-500 text-white' : 'bg-black/40 border-white/5 text-gray-500 hover:border-white/20'}`}
                                    >
                                        <div className="font-bold flex items-center gap-2">
                                            <span className={mode === 'async' ? 'text-purple-400' : ''}>🕰️</span> Async
                                        </div>
                                        <div className="text-xs mt-1 opacity-70">Friends play on their own time. Results drop at 8PM.</div>
                                    </button>
                                </div>
                            </div>

                            {/* Settings */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-400 mb-2 uppercase tracking-wider">Max Players</label>
                                    <select
                                        value={maxPlayers}
                                        onChange={(e) => setMaxPlayers(Number(e.target.value))}
                                        className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[var(--primary)]"
                                    >
                                        <option value={10}>10 Players</option>
                                        <option value={20}>20 Players</option>
                                        <option value={50}>50 Players</option>
                                        <option value={100}>100 Players</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-400 mb-2 uppercase tracking-wider">Rounds</label>
                                    <select
                                        value={maxRounds}
                                        onChange={(e) => setMaxRounds(Number(e.target.value))}
                                        className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[var(--primary)]"
                                    >
                                        <option value={5}>5 Rounds (Quick)</option>
                                        <option value={8}>8 Rounds (Standard)</option>
                                        <option value={12}>12 Rounds (Long)</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8">
                            <button
                                onClick={handleCreate}
                                disabled={isLoading}
                                className="w-full bg-white text-black font-extrabold text-lg py-4 rounded-xl shadow-[0_0_20px_rgba(255,255,255,0.2)] hover:bg-gray-200 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="w-5 h-5 rounded-full border-t-2 border-black animate-spin" />
                                        Creating Lobby...
                                    </>
                                ) : (
                                    session?.user ? "Create Lobby" : "Sign Up to Host"
                                )}
                            </button>
                            {(!session || !session.user) && (
                                <p className="text-center text-xs text-indigo-400 mt-3 font-medium italic opacity-80">
                                    You&apos;ll be redirected to set up your profile first.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
